/**
 * Blocks v4 session identity.
 *
 * Under v4 the app holds no JWT, so identity and org scope can no longer be read
 * from a decoded token. They come from two calls on the session cookie:
 *   GET /iam/v4/iam/me                  → the signed-in user (also the "am I logged in?" check)
 *   GET /iam/v4/iam/organizations/my    → the orgs the caller belongs to
 *
 * `/iam/me` does NOT return an organization, so the active org is the user's own
 * stored selection (the org-select screen), falling back to their sole org.
 *
 * Note the two envelopes differ: users/roles return `{ data }`, organization
 * routes return `{ isSuccess, organizations }`.
 */

import { BLOCKS_ENDPOINTS, blocksHeaders } from "./config";

/**
 * Every field below was read off a live 200 from `GET /iam/v4/iam/me`
 * (verified 2026-07-30, project `D917ade…`). Notably `status` is a **number**
 * (1 = active) and the response carries a single `organizationId` — v4 has no
 * `memberships[]` array on this route the way v1's `Iam/GetUser` did.
 */
export type BlocksMe = {
  itemId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  roles?: string[];
  /**
   * `/iam/me` returns the singular `organizationId`; `GET /iam/users/{id}`
   * returns this plural array instead. `toBlocksUser` reads both.
   */
  organizationIds?: string[];
  permissions?: string[];
  active?: boolean;
  status?: number;
  isVerified?: boolean;
  mfaEnabled?: boolean;
  isMfaVerified?: boolean;
  userMfaType?: number;
  profileImageUrl?: string | null;
  createdDate?: string;
  lastUpdatedDate?: string;
  language?: string;
  salutation?: string;
  logInCount?: number;
  lastLoggedInTime?: string;
  organizationId?: string;
};

export type BlocksOrganization = {
  itemId: string;
  name?: string;
  createdDate?: string;
};

const authedGet = async (url: string): Promise<Response> =>
  fetch(url, { credentials: "include", headers: blocksHeaders() });

const authedPost = async (url: string, body: unknown): Promise<Response> =>
  fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { ...blocksHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

/**
 * Cached `/iam/me`.
 *
 * `decodeToken` is the app's stand-in for reading a JWT and has ~20 call sites,
 * most of which want nothing but the org id to stamp a write with — and every
 * one of them used to cost a round trip. On native the GraphQL client resolves
 * the org the same way before each request, so a screenful of queries opened a
 * screenful of `/iam/me` calls.
 *
 * The answer only changes when the session does, so it is held for the calls
 * that follow one another and thrown away whenever the session moves —
 * sign-in, sign-out, refresh, org switch, all of which reach
 * `clearSessionCache`.
 */
let meCache: { value: BlocksMe | null; at: number } | null = null;
let meInFlight: Promise<BlocksMe | null> | null = null;

/**
 * Long enough to collapse a screen's worth of requests into one, short enough
 * that a change made elsewhere (a role granted in the portal) is picked up
 * without a restart.
 */
const ME_CACHE_TTL_MS = 60_000;

const requestMe = async (): Promise<BlocksMe | null> => {
  const res = await authedGet(BLOCKS_ENDPOINTS.me);
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`/iam/me failed: ${res.status}`);

  const body = (await res.json()) as { data?: BlocksMe };
  return body.data ?? null;
};

/**
 * The signed-in user, or `null` when there is no valid session (401).
 * Call on app load, and again right after the SSO callback sets the cookie.
 *
 * Served from cache unless `force` is set. Pass `force` when the point of the
 * call IS the network — re-reading after an org switch, or asking the server
 * whether the session is still alive.
 */
export const fetchMe = async (
  options?: { force?: boolean },
): Promise<BlocksMe | null> => {
  if (options?.force) {
    clearMeCache();
  } else {
    if (meCache && Date.now() - meCache.at < ME_CACHE_TTL_MS) {
      return meCache.value;
    }
    // Callers that arrive while a request is already out share its answer
    // rather than starting a second one.
    if (meInFlight) return meInFlight;
  }

  meInFlight = requestMe()
    .then((value) => {
      meCache = { value, at: Date.now() };
      return value;
    })
    .finally(() => {
      meInFlight = null;
    });

  return meInFlight;
};

/** Drop the cached identity. Call whenever the session changes. */
export const clearMeCache = (): void => {
  meCache = null;
  meInFlight = null;
};

/**
 * A 200 with a profile means signed in; 401 means signed out.
 *
 * Deliberately uncached: this is the question "is the session still good?",
 * and answering it from a minute-old copy is answering a different question.
 */
export const isSignedIn = async (): Promise<boolean> =>
  (await fetchMe({ force: true })) !== null;

/**
 * v4 active-org switch — `POST /iam/v4/auth/switch-org` with `{organization_id}`.
 * Replaces the v1 `grant_type=switch_organization` password grant.
 *
 * Verified live 2026-07-30: 200 rotates the session cookies so the new
 * access-token cookie carries the new org; `{}` → 400 `organization_id is
 * required`; an unknown id → 400 `organization_not_available`. In browser mode
 * the 200 body has no tokens (only the cookies moved); a non-browser caller gets
 * `access_token` / `refresh_token` in the body, so the result is surfaced for
 * the native path even though web relies purely on the cookie rotation.
 */
export type SwitchOrgResult = {
  access_token?: string;
  refresh_token?: string;
};

export const switchOrg = async (
  organizationId: string,
): Promise<SwitchOrgResult> => {
  const res = await authedPost(BLOCKS_ENDPOINTS.switchOrg, {
    organization_id: organizationId,
  });
  if (!res.ok) {
    // 400 carries {error, error_description}; surface the most specific text.
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      error_description?: string;
    };
    throw new Error(
      body.error_description ||
        body.error ||
        `switch-org failed: ${res.status}`,
    );
  }
  // Clear the cached org so the next resolve reads the rotated cookie's org.
  clearSessionCache();
  return (await res.json().catch(() => ({}))) as SwitchOrgResult;
};

/**
 * Fields `POST /iam/v4/iam/me` accepts. `itemId` is required by validation but
 * ignored as a target (see `updateMe`); the rest must ALL be sent on every call.
 */
export type UpdateMePayload = {
  itemId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  profileImageUrl: string | null;
  salutation: string;
  language: string;
  roles: string[];
};

/**
 * The signed-in user's profile write — `POST /iam/v4/iam/me`.
 *
 * This is the v4 replacement for the dead `POST /idp/v1/Iam/Update`. Verified
 * live 2026-07-30 on an ordinary project-user session: **200**
 * `{itemId, errors, isSuccess}`, and it needs no admin rights at all — it worked
 * even before the project granted IAM read permission.
 *
 * Two behaviours that make this dangerous to call casually:
 *
 * 1. **It is a full replace — omitted fields are CLEARED.** Posting only
 *    `{itemId}` blanked `roles` from `["admin"]` to `[]`, and a later post that
 *    omitted `salutation` / `profileImageUrl` emptied both. `email`, `active`,
 *    `isVerified`, `mfaEnabled` and `status` survived, so identity and security
 *    fields are not writable here. Hence `UpdateMePayload` requires every
 *    writable field: callers must read `/iam/me` first and echo back what they
 *    are not changing. Note this route also accepts `roles` on the caller's own
 *    record, i.e. it will happily let a session rewrite its own roles — another
 *    reason never to omit them by accident.
 * 2. **The body's `itemId` is ignored.** Posting a bogus `itemId` returned 200
 *    with the CALLER's `itemId` echoed back and updated the caller's own record.
 *    "me" is always the session's user, so this can never write to somebody
 *    else — callers must check the id themselves rather than trust the 200.
 */
export const updateMe = async (payload: UpdateMePayload): Promise<void> => {
  const res = await authedPost(BLOCKS_ENDPOINTS.updateMe, payload);
  if (!res.ok) throw new Error(`/iam/me update failed: ${res.status}`);

  const body = (await res.json()) as { isSuccess?: boolean; errors?: unknown };
  if (body.isSuccess === false) {
    throw new Error(`/iam/me update rejected: ${JSON.stringify(body.errors)}`);
  }
};

/**
 * Update ANOTHER user — `POST /iam/v4/iam/users/{itemId}`, with the id in both
 * the path and the body.
 *
 * Verified live 2026-07-30 with a full round-trip on a real user: `roles` went
 * `[]` → `["member"]` → `[]`, returning `200 {itemId, isSuccess}` each time.
 *
 * ⚠ NOT `POST /iam/users/roles-and-permissions`, which the skill docs name as the
 * dedicated grant call: it answers `400 {"ItemId":"Not found"}` for a valid user
 * id with either `userId` or `itemId` — the same dead end as `users/gets`. The
 * doc's `{userId, roles[], permissions[]}` body is wrong on the field name too;
 * every route here wants `ItemId`.
 *
 * Treat it as a full replace like `/iam/me` — callers read the target first and
 * echo back what they are not changing.
 */
export const updateUserById = async (
  itemId: string,
  payload: Omit<UpdateMePayload, "itemId"> & { permissions?: string[] },
): Promise<void> => {
  const res = await authedPost(BLOCKS_ENDPOINTS.user(itemId), {
    itemId,
    ...payload,
  });
  if (!res.ok) throw new Error(`/iam/users/${itemId} update failed: ${res.status}`);

  const body = (await res.json()) as { isSuccess?: boolean; errors?: unknown };
  if (body.isSuccess === false) {
    throw new Error(
      `/iam/users/${itemId} update rejected: ${JSON.stringify(body.errors)}`,
    );
  }
};

/**
 * Another user by id — `GET /iam/v4/iam/users/{itemId}`.
 *
 * **This is NOT `POST /iam/users/gets`.** Once the project granted IAM read
 * rights (2026-07-30) the routes became readable and `users/gets` turned out not
 * to be a user-by-id fetch at all: it answers
 * `400 {"errors":{"ItemId":"Not found"}}` for *valid* user ids — including the
 * caller's own — with or without `projectKey`. `GET /iam/users/{id}` returns
 * `200 {data: {...}}` for the same ids. The earlier "single-user fetch despite
 * the plural" reading came from an impersonated token's `400 "ItemId field is
 * required"`, which only proved the field was required, not that the route did
 * what the name suggested.
 *
 * Shape note: this route returns `roles` as a **flat array** (like `/iam/me`) and
 * carries `organizationIds` (plural). The *list* route returns `roles` as an
 * org-keyed object instead — see `fetchUsers` in `services/users.blocks.api.ts`.
 *
 * Still fail-soft on 401/403 so a profile shows a blank name rather than
 * erroring if the permission is ever revoked.
 */
export const fetchUser = async (itemId: string): Promise<BlocksMe | null> => {
  if (!itemId) return null;

  const res = await authedGet(BLOCKS_ENDPOINTS.user(itemId));
  // 401 = no session, 403 = session without IAM read rights.
  if (res.status === 401 || res.status === 403) return null;
  if (!res.ok) throw new Error(`/iam/users/${itemId} failed: ${res.status}`);

  const body = (await res.json()) as { data?: BlocksMe } & Partial<BlocksMe>;
  const user = body.data ?? (body.itemId ? (body as BlocksMe) : null);
  return user ?? null;
};

/** The caller's organizations. Only valid once logged in — 401 means no session. */
export const fetchMyOrganizations = async (): Promise<BlocksOrganization[]> => {
  const res = await authedGet(BLOCKS_ENDPOINTS.myOrganizations);
  if (res.status === 401) return [];
  if (!res.ok) throw new Error(`/organizations/my failed: ${res.status}`);

  // Named key, not the `{ data }` envelope used elsewhere in IAM.
  const body = (await res.json()) as { organizations?: BlocksOrganization[] };
  return body.organizations ?? [];
};

/**
 * In-memory cache of the resolved org id. Every GraphQL call needs it, and it
 * only changes on login, logout, or an explicit org switch.
 */
let cachedOrgId: string | undefined;

export const setActiveOrgId = (orgId: string | undefined): void => {
  cachedOrgId = orgId;
};

export const clearSessionCache = (): void => {
  cachedOrgId = undefined;
  clearMeCache();
};

/**
 * Active org id, or `undefined` when it cannot be determined (no session, or a
 * multi-org user who hasn't picked yet — the org-select screen handles that).
 *
 * `storedOrgId` is the user's persisted selection; it wins so an explicit switch
 * is honoured. With exactly one org we adopt it silently.
 */
export const resolveActiveOrgId = async (
  storedOrgId?: string,
): Promise<string | undefined> => {
  if (storedOrgId) {
    cachedOrgId = storedOrgId;
    return cachedOrgId;
  }
  if (cachedOrgId) return cachedOrgId;

  const orgs = await fetchMyOrganizations();
  if (orgs.length === 1) {
    cachedOrgId = orgs[0].itemId;
    return cachedOrgId;
  }
  return undefined;
};

/** Cached value only — for callers that must stay synchronous. */
export const getCachedOrgId = (): string | undefined => cachedOrgId;
